const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const REQUEST_TIMEOUT = 10000;

const timeoutPromise = (timeout: number) =>
  new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error(`Request timed out after ${timeout}ms`));
    }, timeout);
  });

export interface DealerCertificatePayload {
  serial: string;
  pin: string;
  modelName: string;
  caseMaterial: string;
  caseHasStones: 'Yes' | 'No';
  casePreciousMaterial: string;
  caseCaratWeight: string;
  caseCut: string;
  movement: string;
  dialType: '' | 'sunray' | 'mother of pearl' | 'diamond dial';
  dialEmblem: string;
  dialColor: string;
  dialPreciousMaterial: string;
  dialCaratWeight: string;
  dialCut: string;
  indexGlassPreciousMaterial: string;
  indexGlassCaratWeight: string;
  indexGlassCut: string;
  strapBracelet: string;
  buckle: string;
  purchaseDate: string;
  warrantyEndDate: string;
  authorizedRepName: string;
  companyAddress: string;
  companyAddress2: string;
}

export const submitDealerCertificate = async (payload: DealerCertificatePayload): Promise<void> => {
  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured.');
  }

  try {
    const response = await Promise.race([
      fetch(`${API_BASE_URL}/dealer/certificates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }),
      timeoutPromise(REQUEST_TIMEOUT),
    ]) as Response;

    if (!response.ok) {
      let errorMessage = `Failed to save certificate data (${response.status})`;
      try {
        const errorBody = await response.json();
        if (errorBody?.error) {
          errorMessage = errorBody.error;
        }
      } catch {
        // Ignore JSON parse errors and fall back to generic message
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    if (!data?.success) {
      throw new Error(data?.error || 'Unexpected response from certificate service.');
    }
  } catch (error: any) {
    if (error?.message) {
      throw error;
    }
    throw new Error('Failed to connect to certificate service.');
  }
};

export const fetchDealerCertificate = async (serial: string, pin: string): Promise<DealerCertificatePayload> => {
  if (!API_BASE_URL) {
    throw new Error('API base URL is not configured.');
  }

  try {
    const params = new URLSearchParams({
      serial: serial.trim(),
      pin: pin.trim(),
    });

    const response = await Promise.race([
      fetch(`${API_BASE_URL}/dealer/certificates?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      timeoutPromise(REQUEST_TIMEOUT),
    ]) as Response;

    if (!response.ok) {
      let errorMessage = `Failed to fetch certificate data (${response.status})`;
      try {
        const errorBody = await response.json();
        if (errorBody?.error) {
          errorMessage = errorBody.error;
        }
      } catch {
        // Ignore JSON parse errors and fall back to generic message
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    if (!data?.success || !data?.data) {
      throw new Error(data?.error || 'Unexpected response from certificate service.');
    }

    return data.data as DealerCertificatePayload;
  } catch (error: any) {
    if (error?.message) {
      throw error;
    }
    throw new Error('Failed to connect to certificate service.');
  }
};
